/**
 * ESPACE CLIENTE — /moi/:token
 * Son client n'a aucun compte, aucun mot de passe, aucune application.
 * Ce lien EST son compte. Elle voit son rendez-vous, l'adresse exacte, et gère tout d'ici.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarPlus, ChevronRight, Clock, Instagram, MapPin, Navigation,
  Phone, RotateCcw, X,
} from "lucide-react";
import { api, messageErreur } from "../api";
import { Btn, Erreur, Micro, Pastille, Squelette, Vide } from "../components/Bits";
import { Sceau } from "../components/Logo";
import { ajouterJours, duree, euros, isoJour, jourCourt, jourLong } from "../lib/format";

/* --------------------------------------------------- sélecteur de créneau */
function SelecteurCreneau({ token, serviceId, surChoix, envoi }) {
  const [jour, setJour] = useState(isoJour());
  const [creneaux, setCreneaux] = useState(null);
  const [ouvert, setOuvert] = useState(true);
  const [choisi, setChoisi] = useState(null);

  const charger = useCallback(async () => {
    setCreneaux(null);
    try {
      const { data } = await api.get(`/moi/${token}/creneaux`, {
        params: { jour, service_id: serviceId },
      });
      setCreneaux(data.creneaux);
      setOuvert(data.ouvert);
    } catch (e) {
      toast.error(messageErreur(e));
      setCreneaux([]);
    }
  }, [token, serviceId, jour]);

  useEffect(() => {
    charger();
  }, [charger]);

  const jours = Array.from({ length: 14 }, (_, i) => ajouterJours(isoJour(), i));

  return (
    <div data-testid="selecteur-creneau">
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 py-1">
        {jours.map((j) => {
          const actif = j === jour;
          return (
            <button
              key={j}
              onClick={() => {
                setJour(j);
                setChoisi(null);
              }}
              data-testid={`espace-jour-${j}`}
              className="shrink-0 px-3.5 py-2.5 text-center cible-tactile"
              style={{
                borderRadius: "var(--radius-md)",
                background: actif ? "var(--accent)" : "var(--surface)",
                color: actif ? "#fff" : "var(--text)",
                border: `1px solid ${actif ? "var(--accent)" : "var(--border)"}`,
                transition: "background-color var(--dur) var(--ease), color var(--dur) var(--ease)",
              }}
            >
              <span className="block text-[11px] font-semibold uppercase tracking-[0.08em] opacity-80">
                {jourCourt(j).split(" ")[0]}
              </span>
              <span className="num block text-[16px] mt-0.5">{j.slice(8, 10)}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-4">
        {creneaux === null ? (
          <Squelette lignes={1} />
        ) : !ouvert ? (
          <p className="text-[14px]" style={{ color: "var(--text-muted)" }}>
            Fermé ce jour-là. Choisis une autre date.
          </p>
        ) : creneaux.length === 0 ? (
          <p className="text-[14px]" style={{ color: "var(--text-muted)" }}>
            Aucune disponibilité ce jour-là.
          </p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {creneaux.map((c) => {
              const actif = choisi === c.iso;
              return (
                <button
                  key={c.iso}
                  onClick={() => setChoisi(c.iso)}
                  data-testid={`espace-creneau-${c.heure}`}
                  className="num h-11 text-[14px] cible-tactile"
                  style={{
                    borderRadius: "var(--radius-md)",
                    background: actif ? "var(--accent)" : "var(--surface)",
                    color: actif ? "#fff" : "var(--text)",
                    border: `1px solid ${actif ? "var(--accent)" : "var(--border)"}`,
                    transition: "background-color var(--dur) var(--ease), color var(--dur) var(--ease)",
                  }}
                >
                  {c.heure}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {choisi && (
        <Btn
          className="w-full mt-5"
          taille="lg"
          charge={envoi}
          onClick={() => surChoix(choisi)}
          data-testid="espace-valider-creneau"
        >
          Valider {jourLong(jour)} à{" "}
          {creneaux?.find((c) => c.iso === choisi)?.heure}
        </Btn>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ page */
export default function EspaceClient() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [mode, setMode] = useState(null); // "deplacer" | "reprendre" | "annuler"
  const [presta, setPresta] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get(`/moi/${token}`);
      setD(data);
      setErreur(null);
    } catch (e) {
      setErreur(messageErreur(e, "Ce lien n'est plus valable."));
    }
  }, [token]);

  useEffect(() => {
    charger();
  }, [charger]);

  const annuler = async () => {
    setEnvoi(true);
    try {
      const { data } = await api.post(`/moi/${token}/annuler`, { rdv_id: d.prochain.id });
      toast.success(
        data.tardif
          ? "Annulation enregistrée. C'était un peu juste : ton institut est prévenu."
          : "Annulation enregistrée. Merci de l'avoir signalée."
      );
      setMode(null);
      charger();
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setEnvoi(false);
    }
  };

  const deplacer = async (iso) => {
    setEnvoi(true);
    try {
      await api.post(`/moi/${token}/deplacer`, { rdv_id: d.prochain.id, start_at: iso });
      toast.success("Ton rendez-vous a bien été déplacé.");
      setMode(null);
      charger();
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setEnvoi(false);
    }
  };

  const reprendre = async (iso) => {
    setEnvoi(true);
    try {
      await api.post(`/moi/${token}/reprendre`, { service_id: presta.id, start_at: iso });
      toast.success("C'est enregistré. À très vite.");
      setMode(null);
      setPresta(null);
      charger();
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setEnvoi(false);
    }
  };

  if (erreur)
    return (
      <div className="min-h-screen flex items-center justify-center pad" style={{ background: "var(--bg)" }}>
        <Vide titre="Ce lien n'est plus valable" texte={erreur} />
      </div>
    );

  if (!d)
    return (
      <div className="min-h-screen pad pt-16" style={{ background: "var(--bg)" }}>
        <div className="max-w-[520px] mx-auto">
          <Squelette lignes={3} />
        </div>
      </div>
    );

  const p = d.prochain;
  const itineraire = d.pro.adresse
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(d.pro.adresse)}`
    : null;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="page-espace-client">
      {/* ------------------------------------------------------------ entête */}
      <header className="ciel">
        <div className="nuage nuage-a" />
        <div className="nuage nuage-e" />
        <div className="pad pt-12 pb-10 max-w-[560px] mx-auto relative">
          <Micro>{d.pro.metier_label}</Micro>
          <h1 className="display text-[30px] md:text-[36px] mt-2" data-testid="espace-titre">
            {d.pro.activite}
          </h1>
          <p className="text-[15px] mt-3" style={{ color: "var(--text-muted)" }}>
            Bonjour {d.client.prenom}. Voici ton espace personnel. Aucun compte, aucun mot de passe : ce lien suffit.
          </p>
        </div>
      </header>

      <main className="pad max-w-[560px] mx-auto pb-20 -mt-4">
        {/* ------------------------------------------------ prochain rendez-vous */}
        {p ? (
          <section
            className="p-5 pose"
            data-testid="espace-prochain"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-lg)",
              boxShadow: "var(--shadow-md)",
            }}
          >
            <div className="flex items-center justify-between gap-3">
              <Micro>Ton rendez-vous</Micro>
              {p.confirme ? (
                <Pastille ton="olive">Confirmé</Pastille>
              ) : (
                <Pastille ton="accent">Réservé</Pastille>
              )}
            </div>

            <div className="display text-[24px] mt-3 first-letter:uppercase">{p.jour_label}</div>
            <div className="flex items-baseline gap-2.5 mt-1.5">
              <span className="num text-[32px] leading-none">{p.heure}</span>
              <span className="text-[13px]" style={{ color: "var(--text-subtle)" }}>
                → {p.fin}
              </span>
            </div>
            <p className="text-[15px] mt-3">
              {p.prestation} · {duree(p.duree_min)} ·{" "}
              <span className="num">{euros(p.prix)}</span>
            </p>

            {/* adresse : révélée parce que le rendez-vous est confirmé */}
            <div className="mt-5 pt-5" style={{ borderTop: "1px solid var(--border)" }}>
              <Micro muted>Le lieu du rendez-vous</Micro>
              {d.pro.adresse ? (
                <>
                  <p className="text-[15px] mt-2 flex items-start gap-2">
                    <MapPin size={16} strokeWidth={1.8} className="mt-0.5 shrink-0" style={{ color: "var(--accent)" }} />
                    <span data-testid="espace-adresse">{d.pro.adresse}</span>
                  </p>
                  {itineraire && (
                    <a
                      href={itineraire}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="espace-itineraire"
                      className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold mt-2.5"
                      style={{ color: "var(--accent)" }}
                    >
                      <Navigation size={14} strokeWidth={2} /> Ouvrir l'itinéraire
                    </a>
                  )}
                </>
              ) : (
                <p className="text-[14px] mt-2" style={{ color: "var(--text-muted)" }}>
                  {d.pro.quartier
                    ? `${d.pro.quartier}. L'adresse exacte te sera envoyée avant le rendez-vous.`
                    : "L'adresse exacte te sera envoyée avant le rendez-vous."}
                </p>
              )}
            </div>

            {/* actions */}
            <div className="flex flex-wrap gap-2 mt-5">
              {d.peut_deplacer && (
                <Btn
                  variante="fantome"
                  icone={Clock}
                  onClick={() => setMode(mode === "deplacer" ? null : "deplacer")}
                  data-testid="espace-btn-deplacer"
                >
                  Déplacer
                </Btn>
              )}
              <Btn
                variante="danger"
                icone={X}
                onClick={() => setMode(mode === "annuler" ? null : "annuler")}
                data-testid="espace-btn-annuler"
              >
                Annuler
              </Btn>
            </div>

            {mode === "annuler" && (
              <div
                className="mt-4 p-4"
                data-testid="espace-bloc-annulation"
                style={{ background: "var(--risk-high-bg)", borderRadius: "var(--radius-md)" }}
              >
                <p className="text-[14px]" style={{ color: "var(--text)" }}>
                  Une annulation à moins de <span className="num">{d.annulation_heures}</span> h du
                  rendez-vous est notée comme tardive : le créneau est difficile à remplacer.
                </p>
                <div className="flex gap-2 mt-3.5">
                  <Btn variante="danger" charge={envoi} onClick={annuler} data-testid="espace-confirmer-annulation">
                    Confirmer l'annulation
                  </Btn>
                  <Btn variante="nu" onClick={() => setMode(null)}>
                    Je garde mon créneau
                  </Btn>
                </div>
              </div>
            )}

            {mode === "deplacer" && (
              <div className="mt-5 pt-5" style={{ borderTop: "1px solid var(--border)" }}>
                <Micro className="mb-3">Choisis un nouveau créneau</Micro>
                <SelecteurCreneau
                  token={token}
                  serviceId={p.service_id}
                  surChoix={deplacer}
                  envoi={envoi}
                />
              </div>
            )}
          </section>
        ) : (
          <section
            className="p-5 pose"
            data-testid="espace-aucun-rdv"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-lg)",
              boxShadow: "var(--shadow-sm)",
            }}
          >
            <Micro>Ton rendez-vous</Micro>
            <p className="text-[17px] mt-2.5 leading-relaxed">
              Tu n'as aucun rendez-vous à venir pour le moment.
              {d.visites > 0 && (
                <>
                  {" "}
                  Tu es venue <span className="num">{d.visites}</span> fois.
                </>
              )}
            </p>
          </section>
        )}

        {/* ----------------------------------------------- reprendre rendez-vous */}
        {d.reservation_ouverte && d.prestations.length > 0 && (
          <section className="mt-8" data-testid="espace-reprendre">
            <Micro className="mb-3">{p ? "Prendre le prochain rendez-vous" : "Prendre rendez-vous"}</Micro>
            <div className="space-y-2">
              {d.prestations.map((s) => {
                const actif = presta?.id === s.id;
                return (
                  <div key={s.id}>
                    <button
                      onClick={() => {
                        setPresta(actif ? null : s);
                        setMode(actif ? null : "reprendre");
                      }}
                      data-testid={`espace-presta-${s.id}`}
                      className="w-full text-left flex items-center gap-3 p-4 cible-tactile"
                      style={{
                        background: "var(--surface)",
                        border: `1px solid ${actif ? "var(--accent)" : "var(--border)"}`,
                        borderRadius: "var(--radius-lg)",
                        transition: "border-color var(--dur) var(--ease)",
                      }}
                    >
                      <span className="flex-1 min-w-0">
                        <span className="block text-[15px] font-semibold truncate">{s.nom}</span>
                        <span className="block text-[13px]" style={{ color: "var(--text-muted)" }}>
                          {duree(s.duree_min)}
                        </span>
                      </span>
                      <span className="num text-[15px] shrink-0">{euros(s.prix)}</span>
                      {actif ? (
                        <RotateCcw size={16} strokeWidth={1.9} style={{ color: "var(--accent)" }} />
                      ) : (
                        <ChevronRight size={16} strokeWidth={1.9} style={{ color: "var(--text-subtle)" }} />
                      )}
                    </button>
                    {actif && mode === "reprendre" && (
                      <div
                        className="mt-2 p-4"
                        style={{
                          background: "var(--surface)",
                          border: "1px solid var(--border)",
                          borderRadius: "var(--radius-lg)",
                        }}
                      >
                        <SelecteurCreneau
                          token={token}
                          serviceId={s.id}
                          surChoix={reprendre}
                          envoi={envoi}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ------------------------------------------------------- mes visites */}
        {d.historique.length > 0 && (
          <section className="mt-8" data-testid="espace-historique">
            <Micro className="mb-3">Mes visites</Micro>
            <div style={{ borderTop: "1px solid var(--border)" }}>
              {d.historique.map((h) => (
                <div
                  key={h.id}
                  className="flex items-center gap-3 py-3.5"
                  style={{ borderBottom: "1px solid var(--border)" }}
                >
                  <span className="flex-1 min-w-0">
                    <span className="block text-[14.5px] font-semibold truncate">{h.prestation}</span>
                    <span className="block text-[13px] first-letter:uppercase" style={{ color: "var(--text-muted)" }}>
                      {h.jour_label} · <span className="num">{h.heure}</span>
                    </span>
                  </span>
                  {h.statut === "absent" ? (
                    <Pastille ton="brique">Absent</Pastille>
                  ) : h.statut === "annule" || h.statut === "annule_tardif" ? (
                    <Pastille ton="neutre">Annulé</Pastille>
                  ) : (
                    <span className="num text-[14px]">{euros(h.prix)}</span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* -------------------------------------------- moyens de paiement */}
        {d.paiements?.length > 0 && (
          <section className="mt-8" data-testid="espace-paiements">
            <Micro className="mb-3">Le règlement</Micro>
            <div
              className="p-4"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-lg)",
              }}
            >
              {d.paiements.map((p) => (
                <div
                  key={p.code}
                  className="py-2.5"
                  style={{ borderBottom: "1px solid var(--border)" }}
                >
                  <div className="text-[14.5px] font-semibold">{p.label}</div>
                  {p.detail && (
                    <div className="text-[13px] mt-0.5 break-all" style={{ color: "var(--text-muted)" }}>
                      {p.detail}
                    </div>
                  )}
                </div>
              ))}
              <p className="text-[12.5px] mt-3" style={{ color: "var(--text-subtle)" }}>
                Le règlement se fait directement auprès de ton institut.
              </p>
            </div>
          </section>
        )}

        {/* ------------------------------------------------------------ contact */}
        <section className="mt-8 flex flex-wrap gap-2" data-testid="espace-contact">
          {d.pro.telephone && (
            <a
              href={`tel:${d.pro.telephone}`}
              className="inline-flex items-center gap-2 h-11 px-4 text-[14px] font-semibold cible-tactile"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-md)",
                color: "var(--text)",
              }}
            >
              <Phone size={15} strokeWidth={1.9} /> Appeler
            </a>
          )}
          {d.pro.instagram && (
            <a
              href={`https://instagram.com/${String(d.pro.instagram).replace("@", "")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 h-11 px-4 text-[14px] font-semibold cible-tactile"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-md)",
                color: "var(--text)",
              }}
            >
              <Instagram size={15} strokeWidth={1.9} /> Instagram
            </a>
          )}
          {d.pro.slug && (
            <a
              href={`/r/${d.pro.slug}`}
              className="inline-flex items-center gap-2 h-11 px-4 text-[14px] font-semibold cible-tactile"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-md)",
                color: "var(--text)",
              }}
            >
              <CalendarPlus size={15} strokeWidth={1.9} /> La page
            </a>
          )}
        </section>

        <p className="text-[12px] mt-8 leading-relaxed" style={{ color: "var(--text-subtle)" }}>
          Garde ce lien : il est personnel et permanent. Il te donne accès à tes rendez-vous, et à
          rien d'autre.
        </p>

        <div className="flex items-center gap-2 mt-6">
          <Sceau taille={15} />
          <span className="text-[11.5px]" style={{ color: "var(--text-subtle)" }}>
            Propulsé par Servae
          </span>
        </div>
      </main>
    </div>
  );
}
